class AnnouncementMarquee extends HTMLElement {
  constructor() {
    super();
    this.wrapper = null;
    this.content = null;
    this.resizeHandler = this.debounce(this.handleResize.bind(this), 250);
  }

  connectedCallback() {
    this.wrapper = this.querySelector('.announcement-bar__scroller');
    this.content = this.querySelector('.announcement-bar__text-wrapper');
    
    if (!this.wrapper || !this.content) return;

    this.addRepeatedItems();
    this.duplicateContent();
    this.setSpeed();

    window.addEventListener('resize', this.resizeHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  get clonedContent() {
    const lastChild = this.wrapper.lastElementChild;
    return this.content !== lastChild ? lastChild : null;
  }

  setSpeed(value = this.calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  calculateSpeed() {
    const speedSetting = this.getAttribute('data-speed') || 'normal';
    const marqueeWidth = this.offsetWidth;
    
    // Speed factors for different settings
    const speedFactors = {
      'slow': 5,     // Much slower scrolling
      'normal': 12,  // Proper medium speed
      'fast': 20     // Current medium-fast speed
    };
    
    const speedFactor = speedFactors[speedSetting] || speedFactors['normal'];
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return Math.max(5, speed); // Minimum 5 seconds
  }

  handleResize() {
    const newNumberOfCopies = this.calculateNumberOfCopies();
    const currentNumberOfCopies = this.content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.duplicateContent();
    this.setSpeed();
    this.restartAnimation();
  }

  restartAnimation() {
    const animations = this.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  duplicateContent() {
    this.clonedContent?.remove();
    const clone = this.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    this.wrapper.appendChild(clone);
  }

  addRepeatedItems(numberOfCopies = this.calculateNumberOfCopies()) {
    const originalItems = Array.from(this.content.children);
    
    for (let i = 0; i < numberOfCopies - 1; i++) {
      originalItems.forEach(item => {
        const clone = item.cloneNode(true);
        this.content.appendChild(clone);
      });
    }
  }

  removeRepeatedItems(numberOfCopies) {
    for (let i = 0; i < numberOfCopies; i++) {
      this.content.lastElementChild?.remove();
    }
  }

  calculateNumberOfCopies() {
    const marqueeWidth = this.offsetWidth;
    const firstChild = this.content.firstElementChild;
    if (!firstChild) return 1;
    
    // Calculate the width of one complete set of text + separator
    const tempContainer = document.createElement('div');
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.position = 'absolute';
    tempContainer.style.whiteSpace = 'nowrap';
    tempContainer.innerHTML = firstChild.outerHTML + (firstChild.nextElementSibling?.outerHTML || '');
    document.body.appendChild(tempContainer);
    const itemWidth = tempContainer.offsetWidth;
    document.body.removeChild(tempContainer);
    
    return Math.max(1, Math.ceil(marqueeWidth / itemWidth) + 2); // +2 for smooth overflow
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
} 